/**
 * The printed statement of account — كشف حساب.
 *
 * One A4 sheet (it runs on to more pages by itself) the shop can hand or send a
 * customer: its letterhead, who the statement is for and for which period, and
 * per currency the balance brought forward, every invoice, credit note, payment
 * and receipt voucher in date order with the running balance, the closing
 * balance, and how old what is still owed is.
 *
 * The lines come from GET /parties/:id/statement/lines — a ledger built from
 * the money documents themselves, so it closes on the same balance the
 * customer's page, the list, the POS and the customer's own app show. A
 * branch-confined person prints their branches' account only (the server
 * scopes it). Styled like the A42 invoice, so the shop's papers match.
 */
import type { Party, PortalStatement } from '@erp/shared';
import { ArrowLeft, Printer } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { assetUrl } from '../../lib/publicApi.js';

type LineKind = 'invoice' | 'credit_note' | 'payment' | 'voucher' | 'refund';

interface Ledger {
  from: string | null;
  to: string;
  currencies: Array<{
    currencyCode: string;
    opening: string;
    debit: string;
    credit: string;
    closing: string;
    lines: Array<{
      date: string; kind: LineKind; id: string; number: string; note: string | null; branch: string | null;
      debit: string; credit: string; balance: string;
    }>;
  }>;
}

export interface Letterhead {
  name: string;
  baseCurrencyCode: string;
  seller: {
    storeName: string | null;
    tagline: string | null;
    logoAttachmentId: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    socialLinks?: { website?: string | null } | null;
    address: Record<string, unknown> | null;
    slug: string;
  } | null;
}

const ADDRESS_ORDER = ['line1', 'line2', 'street', 'district', 'city', 'region', 'postalCode', 'country'];
export function addressText(addr: Record<string, unknown> | null | undefined): string {
  if (!addr) return '';
  const keys = [...ADDRESS_ORDER.filter((k) => k in addr), ...Object.keys(addr).filter((k) => !ADDRESS_ORDER.includes(k))];
  const parts = keys.map((k) => addr[k]).filter((v): v is string => typeof v === 'string' && v.trim().length > 0).map((v) => v.trim());
  return parts.join(parts.some((v) => /[؀-ۿ]/.test(v)) ? '، ' : ', ');
}

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function CustomerStatementPrintPage(): JSX.Element {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { i18n, t: tUi } = useTranslation('parties');
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? localToday();
  // The paper's own language — Arabic and English always offered, plus the
  // app's language when it is neither.
  const langs = useMemo(() => [...new Set(['ar', 'en', i18n.language.split('-')[0]!])], [i18n.language]);
  const [lang, setLang] = useState(params.get('lang') ?? i18n.language.split('-')[0] ?? 'ar');
  const t = useMemo(() => i18n.getFixedT(lang, 'parties'), [i18n, lang]);
  const dir = i18n.dir(lang);

  const [party, setParty] = useState<Party | null>(null);
  const [ledger, setLedger] = useState<Ledger | null>(null);
  const [aging, setAging] = useState<PortalStatement['aging']>([]);
  const [head, setHead] = useState<Letterhead | null>(null);
  const [failed, setFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let on = true;
    const q = new URLSearchParams({ to, ...(from ? { from } : {}) }).toString();
    void Promise.all([
      api().get<Party>(`/parties/${id}`),
      api().get<Ledger>(`/parties/${id}/statement/lines?${q}`),
    ])
      .then(([p, l]) => { if (on) { setParty(p.data); setLedger(l.data); } })
      .catch(() => { if (on) setFailed(true); });
    // Best-effort: the paper prints without an aging table or a logo.
    void api().get<PortalStatement>(`/parties/${id}/statement?to=${to}`)
      .then((s) => { if (on) setAging(s.data.aging); })
      .catch(() => undefined);
    void api().get<Letterhead>('/tenants/current/paper')
      .then((h) => { if (on) setHead(h.data); })
      .catch(() => undefined);
    return () => { on = false; };
  }, [id, from, to]);

  const setRange = (key: 'from' | 'to', value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const money = (a: string, ccy: string): string => formatMoney(a, { locale: lang, currency: ccy });
  // Calendar dates (YYYY-MM-DD) — read and printed in UTC so no timezone moves them a day.
  const day = (d: string): string => formatDate(d, { locale: lang, timeZone: 'UTC' });
  const seller = head?.seller ?? null;
  const shopName = seller?.storeName?.trim() || head?.name || '';
  const logo = !logoFailed && seller?.logoAttachmentId && seller.slug
    ? assetUrl(`/api/v1/storefront/${seller.slug}/media/${seller.logoAttachmentId}`)
    : null;
  const website = (seller?.socialLinks?.website ?? '').trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
  const shopAddress = addressText(seller?.address);
  const customerAddress = addressText(party?.billingAddress as Record<string, unknown> | null | undefined);
  const hasBranch = (ledger?.currencies ?? []).some((c) => c.lines.some((l) => l.branch));
  const agingRows = aging.filter((a) => Number(a.total) > 0);

  return (
    <div className="sp-root" dir={i18n.dir()}>
      <style>{CSS}</style>
      <div className="sp-bar no-print">
        <Link to={`/sales/parties/${id}`} className="sp-btn"><ArrowLeft className="h-4 w-4" aria-hidden />{tUi('statementPrint.back')}</Link>
        <label className="sp-field">{t('statementPrint.from')}
          <input type="date" value={from} onChange={(e) => setRange('from', e.target.value)} />
        </label>
        <label className="sp-field">{t('statementPrint.to')}
          <input type="date" value={to} onChange={(e) => setRange('to', e.target.value)} />
        </label>
        <label className="sp-field">{tUi('statementPrint.language')}
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            {langs.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </label>
        <button type="button" className="sp-btn primary" onClick={() => window.print()} disabled={!ledger}>
          <Printer className="h-4 w-4" aria-hidden />{tUi('statementPrint.print')}
        </button>
      </div>

      {failed ? (
        <p className="sp-msg">{tUi('statementPrint.failed')}</p>
      ) : !party || !ledger ? (
        <p className="sp-msg">…</p>
      ) : (
        <div className="sp-page" dir={dir} lang={lang}>
          <header className="sp-head">
            <div className="sp-seller">
              {logo ? <img className="sp-logo" src={logo} alt={shopName} onError={() => setLogoFailed(true)} />
                : <div className="sp-mk" aria-hidden>{shopName.slice(0, 2)}</div>}
              <div>
                <div className="sp-shop">{shopName}</div>
                {seller?.tagline?.trim() && <div className="sp-tag">{seller.tagline.trim()}</div>}
                <div className="sp-contact">
                  {seller?.contactPhone && <span dir="ltr">{seller.contactPhone}</span>}
                  {seller?.contactEmail && <span dir="ltr">{seller.contactEmail}</span>}
                  {website && <span dir="ltr">{website}</span>}
                </div>
                {shopAddress && <div className="sp-contact"><bdi>{shopAddress}</bdi></div>}
              </div>
            </div>
            <h1 className="sp-title">{t('statementPrint.title')}</h1>
          </header>

          <div className="sp-meta">
            <div><span className="k">{t('statementPrint.code')}</span><span className="v" dir="ltr">{party.code}</span></div>
            <div><span className="k">{t('statementPrint.period')}</span>
              <span className="v">{ledger.from ? `${day(ledger.from)} — ${day(ledger.to)}` : `${t('statementPrint.allTime')} — ${day(ledger.to)}`}</span></div>
            <div><span className="k">{t('statementPrint.printedOn')}</span><span className="v">{day(localToday())}</span></div>
          </div>

          <div className="sp-cust">
            <div className="k">{t('statementPrint.customer')}</div>
            <div className="nm">{party.displayName}</div>
            {party.phone && <div className="ln" dir="ltr">{party.phone}</div>}
            {customerAddress && <div className="ln"><bdi>{customerAddress}</bdi></div>}
          </div>

          {ledger.currencies.length === 0 && <p className="sp-empty">{t('statementPrint.noMovements')}</p>}

          {ledger.currencies.map((c) => {
            const closing = Number(c.closing);
            return (
              <section key={c.currencyCode} className="sp-ccy">
                <div className="sp-sum">
                  <div><span className="k">{t('statementPrint.opening')}</span><span className="v" dir="ltr">{money(c.opening, c.currencyCode)}</span></div>
                  <div><span className="k">{t('statementPrint.debits')}</span><span className="v" dir="ltr">{money(c.debit, c.currencyCode)}</span></div>
                  <div><span className="k">{t('statementPrint.credits')}</span><span className="v" dir="ltr">{money(c.credit, c.currencyCode)}</span></div>
                  <div className="due">
                    <span className="k">{closing < 0 ? t('statementPrint.inCredit') : t('statementPrint.amountDue')}</span>
                    <span className="v" dir="ltr">{money(String(Math.abs(closing)), c.currencyCode)}</span>
                  </div>
                </div>
                <table className="sp-lines">
                  <thead>
                    <tr>
                      <th className="n">#</th>
                      <th>{t('statementPrint.cols.date')}</th>
                      <th>{t('statementPrint.cols.document')}</th>
                      <th>{t('statementPrint.cols.type')}</th>
                      {hasBranch && <th>{t('statementPrint.cols.branch')}</th>}
                      <th className="m">{t('statementPrint.cols.debit')}</th>
                      <th className="m">{t('statementPrint.cols.credit')}</th>
                      <th className="m">{t('statementPrint.cols.balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.from && (
                      <tr className="open">
                        <td className="n" />
                        <td>{day(ledger.from)}</td>
                        <td colSpan={hasBranch ? 3 : 2}>{t('statementPrint.opening')}</td>
                        <td className="m" />
                        <td className="m" />
                        <td className="m" dir="ltr">{money(c.opening, c.currencyCode)}</td>
                      </tr>
                    )}
                    {c.lines.map((l, i) => (
                      <tr key={`${l.kind}-${l.id}`}>
                        <td className="n">{i + 1}</td>
                        <td>{day(l.date)}</td>
                        <td dir="ltr" className="doc">{l.number}{l.note ? <span className="note"> · <bdi>{l.note}</bdi></span> : null}</td>
                        <td>{t(`statementPrint.kinds.${l.kind}`)}</td>
                        {hasBranch && <td>{l.branch ?? '—'}</td>}
                        <td className="m" dir="ltr">{Number(l.debit) ? money(l.debit, c.currencyCode) : ''}</td>
                        <td className="m" dir="ltr">{Number(l.credit) ? money(l.credit, c.currencyCode) : ''}</td>
                        <td className="m b" dir="ltr">{money(l.balance, c.currencyCode)}</td>
                      </tr>
                    ))}
                    {c.lines.length === 0 && (
                      <tr><td colSpan={hasBranch ? 8 : 7} className="none">{t('statementPrint.noMovements')}</td></tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={hasBranch ? 5 : 4}>{t('statementPrint.totals')}</td>
                      <td className="m" dir="ltr">{money(c.debit, c.currencyCode)}</td>
                      <td className="m" dir="ltr">{money(c.credit, c.currencyCode)}</td>
                      <td className="m" dir="ltr">{money(c.closing, c.currencyCode)}</td>
                    </tr>
                  </tfoot>
                </table>
              </section>
            );
          })}

          {agingRows.length > 0 && (
            <section className="sp-aging">
              <h2>{t('statementPrint.aging')}</h2>
              <table className="sp-lines">
                <thead>
                  <tr>
                    <th>{t('statementPrint.currency')}</th>
                    <th className="m">{t('statementPrint.ages.current')}</th>
                    <th className="m">1–30</th>
                    <th className="m">31–60</th>
                    <th className="m">61–90</th>
                    <th className="m">90+</th>
                    <th className="m">{t('statementPrint.totals')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agingRows.map((a) => (
                    <tr key={a.currencyCode}>
                      <td dir="ltr">{a.currencyCode}</td>
                      <td className="m" dir="ltr">{money(a.current, a.currencyCode)}</td>
                      <td className="m" dir="ltr">{money(a.days_1_30, a.currencyCode)}</td>
                      <td className="m" dir="ltr">{money(a.days_31_60, a.currencyCode)}</td>
                      <td className="m" dir="ltr">{money(a.days_61_90, a.currencyCode)}</td>
                      <td className="m" dir="ltr">{money(a.days_90_plus, a.currencyCode)}</td>
                      <td className="m b" dir="ltr">{money(a.total, a.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <p className="sp-review">{t('statementPrint.review')}</p>
          <div className="sp-sign">
            <div><span className="line" /><span className="cap">{t('statementPrint.signCustomer')}</span></div>
            <div><span className="line" /><span className="cap">{t('statementPrint.signAccountant')}</span></div>
          </div>
          <footer className="sp-foot">
            <div className="ty">{t('statementPrint.thankYou')}</div>
            <div className="ct">
              <span className="nm">{shopName}</span>
              {seller?.contactPhone && <span dir="ltr">{seller.contactPhone}</span>}
              {website && <span dir="ltr">{website}</span>}
            </div>
          </footer>
        </div>
      )}
    </div>
  );
}

const CSS = `
.sp-root { min-height: 100vh; background: #e9ecf2; padding: 16px 12px 40px; }
.sp-bar { max-width: 794px; margin: 0 auto 12px; display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px;
  background: #fff; border: 1px solid #e3e6ee; border-radius: 10px; padding: 10px 12px; color: #16233b; font-size: 12px; }
.sp-btn { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 12px; border-radius: 8px;
  border: 1px solid #d5dae5; background: #fff; color: #16233b; font-weight: 600; cursor: pointer; }
.sp-btn.primary { background: #16233b; color: #fff; border-color: #16233b; margin-inline-start: auto; }
.sp-btn:disabled { opacity: .5; cursor: default; }
.sp-field { display: flex; flex-direction: column; gap: 3px; font-weight: 600; color: #545b72; }
.sp-field input, .sp-field select { height: 34px; border: 1px solid #d5dae5; border-radius: 8px; padding: 0 8px; color: #16233b; background: #fff; }
.sp-msg { text-align: center; color: #545b72; padding: 40px; }

.sp-page { width: 794px; max-width: 100%; min-height: 1123px; margin: 0 auto; background: #fff; color: #3a4252;
  padding: 34px 36px 28px; position: relative; box-shadow: 0 6px 30px rgba(22,35,59,.12);
  font-family: var(--font-sans-arabic), var(--font-sans), system-ui, sans-serif; font-size: 11px; }
.sp-page::before { content: ''; position: absolute; top: 0; inset-inline: 0; height: 6px; background: #16233b;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sp-head { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.sp-seller { display: flex; align-items: center; gap: 14px; min-width: 0; }
.sp-logo { width: 78px; max-height: 78px; object-fit: contain; }
.sp-mk { width: 60px; height: 60px; border-radius: 12px; background: #16233b; color: #fff; display: grid; place-items: center;
  font-size: 20px; font-weight: 800; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sp-shop { font-size: 17px; font-weight: 800; color: #16233b; line-height: 1.25; }
.sp-tag { font-size: 10px; color: #6b7489; }
.sp-contact { font-size: 9.5px; color: #545b72; display: flex; flex-wrap: wrap; gap: 2px 12px; margin-top: 2px; }
.sp-title { margin: 0; font-size: 21px; font-weight: 800; color: #16233b; letter-spacing: .04em; white-space: nowrap; }

.sp-meta { display: flex; margin-top: 16px; border: 1px solid #e3e6ee; border-radius: 8px; overflow: hidden; }
.sp-meta > div { flex: 1 1 0; display: flex; flex-direction: column; gap: 1px; padding: 7px 12px; min-width: 0; }
.sp-meta > div + div { border-inline-start: 1px solid #e3e6ee; }
.sp-meta .k, .sp-cust .k, .sp-sum .k { font-size: 8.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #8a92a6; }
.sp-meta .v { font-size: 11.5px; font-weight: 800; color: #16233b; }

.sp-cust { margin: 14px 0; padding: 10px 14px; background: #f6f8fc; border: 1px solid #e3e6ee; border-inline-start: 3px solid #16233b;
  border-radius: 8px; width: 55%; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sp-cust .nm { font-size: 14px; font-weight: 800; color: #16233b; }
.sp-cust .ln { font-size: 10.5px; color: #545b72; }

.sp-ccy { margin-top: 14px; break-inside: auto; }
.sp-sum { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 8px; }
.sp-sum > div { border: 1px solid #e3e6ee; border-radius: 8px; padding: 7px 10px; display: flex; flex-direction: column; gap: 2px; }
.sp-sum .v { font-size: 12.5px; font-weight: 800; color: #16233b; font-variant-numeric: tabular-nums; text-align: start; }
.sp-sum .due { background: #16233b; border-color: #16233b; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sp-sum .due .k { color: #c9d1e3; }
.sp-sum .due .v { color: #fff; }

.sp-lines { width: 100%; border-collapse: collapse; font-size: 10.5px; }
.sp-lines th { background: #16233b; color: #fff; font-weight: 700; padding: 6px; text-align: start;
  -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sp-lines td { padding: 5px 6px; border-top: 1px solid #edf0f6; vertical-align: top; }
.sp-lines tbody tr:nth-child(even) td { background: #f8fafd; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.sp-lines .n { width: 26px; color: #8a92a6; text-align: center; }
.sp-lines .m { text-align: end; white-space: nowrap; font-variant-numeric: tabular-nums; }
.sp-lines .b { font-weight: 800; color: #16233b; }
.sp-lines .doc { text-align: start; font-weight: 600; color: #16233b; }
.sp-lines .note { font-weight: 400; color: #8a92a6; }
.sp-lines tr.open td { font-style: italic; color: #545b72; }
.sp-lines .none { text-align: center; color: #8a92a6; padding: 12px; }
.sp-lines tfoot td { border-top: 2px solid #16233b; font-weight: 800; color: #16233b; padding: 6px; }
.sp-lines thead { display: table-header-group; }
.sp-lines tr { break-inside: avoid; }

.sp-aging { margin-top: 18px; break-inside: avoid; }
.sp-aging h2 { margin: 0 0 6px; font-size: 11px; font-weight: 800; color: #16233b; text-transform: uppercase; letter-spacing: .06em; }
.sp-empty { color: #8a92a6; text-align: center; padding: 24px; }
.sp-review { margin: 18px 0 0; font-size: 10px; color: #545b72; }
.sp-sign { display: flex; justify-content: space-between; gap: 40px; margin-top: 34px; break-inside: avoid; }
.sp-sign > div { flex: 0 1 220px; display: flex; flex-direction: column; align-items: center; gap: 4px; }
.sp-sign .line { display: block; width: 100%; height: 24px; border-bottom: 1px solid #9aa3b5; }
.sp-sign .cap { font-size: 9.5px; color: #545b72; font-weight: 700; }
.sp-foot { margin-top: 22px; padding-top: 10px; border-top: 1px solid #e3e6ee; text-align: center; break-inside: avoid; }
.sp-foot .ty { font-size: 11px; font-weight: 800; color: #16233b; }
.sp-foot .ct { margin-top: 3px; display: flex; flex-wrap: wrap; justify-content: center; gap: 2px 14px; font-size: 9px; color: #6b7489; }
.sp-foot .ct .nm { font-weight: 700; color: #3a4252; }

@media print {
  @page { size: A4; margin: 10mm; }
  .no-print { display: none !important; }
  .sp-root { background: #fff; padding: 0; min-height: 0; }
  .sp-page { box-shadow: none; width: auto; min-height: 0; padding: 0; }
  .sp-page::before { display: none; }
}
`;
